<template>
  <div class="recipients-container">
    <div class="recipients-container-row">
      <FriedH5 class="recipient-column">{{ recipientsTitle }}</FriedH5>
      <FriedH5 v-if="isSigningOrder" class="signing-order-column">{{
        $t('signing-order')
      }}</FriedH5>
      <FriedH5 class="status-column">{{ $t('status') }}</FriedH5>
    </div>
    <div
      v-for="(recipientItem, index) in recipientsOrderSorted"
      :key="recipientItem.id"
      class="recipients-container-row"
    >
      <div class="recipient-column">
        <div class="recipient-box">
          <FriedAvatar :name="name(recipientItem)" :thumb-url="recipientItem.thumbUrl" />
          <div>
            <div
              v-tooltip="{
                content: getRoleTooltip(recipient.role),
                distance: 8,
              }"
            >
              <FriedH4>{{ name(recipientItem) }}</FriedH4>
              <FriedParagraph>{{ role(recipientItem) }}</FriedParagraph>
            </div>
            <div v-if="showSsn(recipientItem.id)" class="recipient-ssn-number-container">
              <div class="recipient-ssn-number">
                <FriedParagraph :size="ParagraphSize.Small">
                  {{ `${$t('social-security-number')}:` }}
                </FriedParagraph>
                <FriedParagraph :size="ParagraphSize.Small">
                  {{ recipientSSN(recipientItem.id) }}
                </FriedParagraph>
              </div>
              <FriedButton
                v-tooltip="{
                  content: getCopyURLTooltip(recipientSSN(recipientItem.id)),
                  placement: Placement.TopStart,
                  distance: 8,
                }"
                :button-type="ButtonType.Tertiary"
                data-external="recipients-timeline-copy-button"
                :show-loader-text="true"
                @click="handleCopy(recipientSSN(recipientItem.id))"
              >
                {{ getCopyButtonText(recipientSSN(recipientItem.id)) }}
                <template #leftIcon><FriedIcon icon="copy" /></template>
              </FriedButton>
            </div>
          </div>
        </div>
      </div>
      <div v-if="isSigningOrder" class="signing-order-column">
        <div
          :class="[
            'line',
            {
              last: recipientsOrderSorted.length - 1 === index,
              checked: checked(recipientItem),
            },
          ]"
        >
          <div class="order-number">
            {{ recipientItem.orderNum }}
          </div>
        </div>
      </div>
      <div class="status-column">
        <FriedStatusBadge
          :text="getRecipientStatusTranslation(recipientItem.status, true)"
          :status-type="getRecipientStatusType(recipientItem.status)"
          :border="getRecipientHasBadgeBorder(recipientItem.status)"
        />
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { RecipientStatus } from '@getaccept/lib-shared/src/recipients/enums/recipient-status';
import { translateEnum } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { RecipientRole } from '@getaccept/lib-shared/src/recipients/enums/recipient-role';
import type { SigningPageRecipient } from '@getaccept/lib-shared/src/types/signing-page-recipient';
import { ParagraphSize, ButtonType, Placement } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, ref } from 'vue';
import { StatusHelper } from '@getaccept/lib-shared/src/helpers/status.helper';
import copy from 'copy-to-clipboard';
import { useI18n } from 'vue-i18n';
import { RecipientsHelper } from '@getaccept/lib-shared/src/recipients/helpers/recipients.helper';
import type { RecipientSSN } from '../../api/documents/types/recipient-ssn';

export default defineComponent({
  props: {
    recipients: {
      type: Array as PropType<SigningPageRecipient[]>,
      default: () => [],
    },
    isSigningOrder: { type: Boolean },
    showNumRecipients: {
      type: Number,
      default: 3,
    },
    rowHeight: {
      type: Number,
      default: 6,
    },
    recipient: {
      type: Object as PropType<SigningPageRecipient>,
      required: true,
    },
    recipientsSsn: {
      type: Array as PropType<RecipientSSN[]>,
      default: () => [],
    },
    ssnFetched: { type: Boolean },
  },
  setup(props) {
    const { t } = useI18n();
    const recipientsTitle = computed(
      (): string => `${t('recipients')} (${props.recipients.length})`
    );
    const recipientsOrderSorted = computed((): SigningPageRecipient[] =>
      [...props.recipients].sort(
        (recipient1: SigningPageRecipient, recipient2: SigningPageRecipient) =>
          recipient1.orderNum < recipient2.orderNum ? -1 : 1
      )
    );
    const name = (recipient: SigningPageRecipient): string => {
      if (recipient.fullName) {
        return recipient.fullName;
      } else if (recipient.email) {
        return recipient.email;
      } else {
        return recipient.mobile;
      }
    };
    const checked = (recipient: SigningPageRecipient): boolean => {
      if (recipient.role === RecipientRole.CC && recipient.status === RecipientStatus.Reviewed) {
        return true;
      }
      const checkedStatuses: RecipientStatus[] = [RecipientStatus.Signed, RecipientStatus.Approved];
      return checkedStatuses.some((status: RecipientStatus) => status === recipient.status);
    };
    const title = (recipient: SigningPageRecipient): string => {
      const { title, companyName } = recipient;
      if (title && companyName) {
        return `${title} ${t('role_at')} ${companyName}`;
      }
      if (title) {
        return title;
      }
      if (companyName) {
        return companyName;
      }
    };
    const role = (recipient: SigningPageRecipient): string => {
      switch (recipient.role) {
        case RecipientRole.CC:
          return t('recipient-role-cc');
        case RecipientRole.Signer:
          return t('recipient-role-signer');
        case RecipientRole.ExternalApprover:
          if (RecipientsHelper.isInternalApprover(props.recipient.role)) {
            return t('recipient-role-external-approver');
          }
          return t('recipient-role-approver');
        case RecipientRole.Approver:
        case RecipientRole.InternalApproverAPIFallback:
          if (RecipientsHelper.isInternalApprover(props.recipient.role)) {
            return t('recipient-role-internal-approver');
          }
          return t('recipient-role-approver');
        default:
          return '-';
      }
    };
    const getRoleTooltip = (role: RecipientRole) => {
      switch (role) {
        case RecipientRole.Signer:
          return `
          <li>${t('signer-role-description-bullet-one')}</li>
          <li>${t('recipient-role-description-visible-bullet')}</li>
          <li>${t('recipient-role-description-certificate-bullet')}</li>
        `;
        case RecipientRole.Approver:
        case RecipientRole.InternalApproverAPIFallback:
          return `
          <li>${t('internal-approver-role-description-bullet-one')}</li>
          <li>${t('recipient-role-description-not-visible-bullet')}</li>
          <li>${t('recipient-role-description-not-certificate-bullet')}</li>
        `;
        case RecipientRole.ExternalApprover:
          return `
          <li>${t('external-approver-role-description-bullet-one')}</li>
          <li>${t('recipient-role-description-visible-bullet')}</li>
          <li>${t('recipient-role-description-certificate-bullet')}</li>
        `;
        case RecipientRole.CC:
          return `
          <li>${t('viewer-role-description-bullet-one')}</li>
          <li>${t('recipient-role-description-visible-bullet')}</li>
          <li>${t('recipient-role-description-certificate-bullet')}</li>
        `;
      }
    };

    const copiedSsn = ref('');

    const handleCopy = (ssn: string) => {
      copy(ssn);
      copiedSsn.value = ssn;
      setTimeout(() => (copiedSsn.value = ''), 1000);
    };

    const getCopyURLTooltip = (ssn: string) =>
      copiedSsn.value === ssn
        ? t('copied_social_security_number_to_clipboard')
        : t('copy_social_security_number_to_clipboard');

    const getCopyButtonText = (ssn: string) => (copiedSsn.value === ssn ? t('copied') : t('copy'));

    const recipientSSN = (recipientId: string): string =>
      props.recipientsSsn.find(ssnObject => ssnObject.recipientId === recipientId)?.ssn;

    const showSsn = (recipientId: string) => props.ssnFetched && !!recipientSSN(recipientId);
    const status = (recipient: SigningPageRecipient): string =>
      recipient.status ? translateEnum(recipient.status, 'recipient-status', RecipientStatus) : '-';
    const { getRecipientStatusTranslation, getRecipientStatusType, getRecipientHasBadgeBorder } =
      StatusHelper;
    return {
      RecipientStatus,
      ParagraphSize,
      recipientsTitle,
      recipientsOrderSorted,
      name,
      checked,
      title,
      role,
      getRoleTooltip,
      status,
      getRecipientStatusTranslation,
      getRecipientStatusType,
      getRecipientHasBadgeBorder,
      recipientSSN,
      handleCopy,
      getCopyURLTooltip,
      showSsn,
      getCopyButtonText,
      ButtonType,
      Placement,
    };
  },
});
</script>
<style lang="scss" scoped>
$order-number-size: 24px;

.recipients-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-150);

  &-row {
    display: flex;
    gap: var(--spacing-100);
    justify-content: space-between;

    .recipient-column {
      flex-basis: 60%;

      .recipient-box {
        display: flex;
        gap: var(--spacing-100);
        width: 100%;

        .recipient-ssn-number-container {
          display: flex;
          gap: var(--spacing-50);
          padding-top: var(--spacing-25);

          .recipient-ssn-number {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: var(--spacing-25);
          }
        }
      }
    }

    .signing-order-column {
      width: 30%;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      text-align: center;

      .line {
        height: calc(100% + var(--spacing-150));
        border-left: 1px dashed var(--jade-35);
        position: absolute;
        top: 0%;

        .order-number {
          position: absolute;
          height: $order-number-size;
          width: $order-number-size;
          border-radius: 100%;
          background-color: var(--jade-35);
          z-index: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          color: var(--white);
          top: 0;
          transform: translate(-50%, 0);
        }

        &:not(.checked) .order-number {
          border: 1px solid var(--gray-80);
          background-color: var(--gray-96);
          color: var(--text-gray);
        }

        &:not(.checked) {
          border-color: var(--gray-80);
        }

        &.last {
          border: none;
        }
      }
    }

    .status-column {
      width: 10%;
      display: flex;
      justify-content: center;
      align-items: flex-start;
    }

    @media only screen and (max-width: $xs) {
      padding-right: var(--spacing-100);

      .recipient-column {
        .recipient-box {
          .recipient-ssn-number-container {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      }

      .signing-order-column,
      .status-column {
        width: 20%;
      }
    }
  }
}
</style>
